import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface AccessibilityConfig {
  fontSize: 'small' | 'normal' | 'large' | 'extra-large';
}

@Injectable({
  providedIn: 'root'
})
export class AccessibilityService {
  private readonly COOKIE_NAME = 'accessibility-config';
  private readonly COOKIE_EXPIRY_DAYS = 365;
  private configSubject = new BehaviorSubject<AccessibilityConfig>(this.obtenerConfigPorDefecto());
  
  public configuracion$ = this.configSubject.asObservable();

  constructor() {
    this.cargarConfiguracion();
    this.aplicarConfiguracion(this.configSubject.value);
  }

  private obtenerConfigPorDefecto(): AccessibilityConfig {
    return {
      fontSize: 'normal'
    };
  }

  private cargarConfiguracion(): void {
    try {
      const cookieValue = this.obtenerCookie(this.COOKIE_NAME);
      if (cookieValue) {
        const configuracion = JSON.parse(cookieValue) as AccessibilityConfig;
        this.configSubject.next({ ...this.obtenerConfigPorDefecto(), ...configuracion });
      }
    } catch (error) {
      console.warn('Error al cargar configuración de accesibilidad desde cookie:', error);
    }
  }

  public actualizarConfiguracion(config: Partial<AccessibilityConfig>): void {
    const configuracionActual = this.configSubject.value;
    const nuevaConfiguracion = { ...configuracionActual, ...config };
    
    this.configSubject.next(nuevaConfiguracion);
    this.guardarConfiguracion(nuevaConfiguracion);
    this.aplicarConfiguracion(nuevaConfiguracion);
  }

  private guardarConfiguracion(config: AccessibilityConfig): void {
    try {
      const configJson = JSON.stringify(config);
      this.establecerCookie(this.COOKIE_NAME, configJson, this.COOKIE_EXPIRY_DAYS);
    } catch (error) {
      console.warn('Error al guardar configuración de accesibilidad en cookie:', error);
    }
  }

  private aplicarConfiguracion(config: AccessibilityConfig): void {
    const root = document.documentElement;

    root.classList.remove('font-size-small', 'font-size-normal', 'font-size-large', 'font-size-extra-large');
    root.classList.add(`font-size-${config.fontSize}`);
  }

  public obtenerConfiguracionActual(): AccessibilityConfig {
    return this.configSubject.value;
  }

  public restablecerPorDefecto(): void {
    const configPorDefecto = this.obtenerConfigPorDefecto();
    this.actualizarConfiguracion(configPorDefecto);
  }

  private establecerCookie(nombre: string, valor: string, dias: number): void {
    const fecha = new Date();
    fecha.setTime(fecha.getTime() + (dias * 24 * 60 * 60 * 1000));
    const expiracion = "expires=" + fecha.toUTCString();
    document.cookie = `${nombre}=${valor};${expiracion};path=/;SameSite=Strict`;
  }

  private obtenerCookie(nombre: string): string | null {
    const nombreCookie = nombre + "=";
    const cookies = document.cookie.split(';');
    
    for (let i = 0; i < cookies.length; i++) {
      let cookie = cookies[i];
      while (cookie.charAt(0) === ' ') {
        cookie = cookie.substring(1);
      }
      if (cookie.indexOf(nombreCookie) === 0) {
        return cookie.substring(nombreCookie.length, cookie.length);
      }
    }
    return null;
  }

  private eliminarCookie(nombre: string): void {
    document.cookie = `${nombre}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
  }
}